import * as z from "zod";

import type { Contract, Target } from "../types";
import { omsAutomationCodeStepOutputSchema } from "./oms-automation-actions";

/**
 * Input universe for the oms-automation.code-step contract.
 *
 * Shape mirrors the underlying FulfillmentGroup mongo document for clarity:
 * fields live where they live in the source, not where they "feel right" to
 * the author. Where convenience and source disagree, this contract sides with
 * source so that loading is a 1:1 projection and authors who reach for the
 * data model docs don't have to re-derive the mapping.
 *
 * Top-level:
 *   - `order`: the Shopify order being processed plus the FO-level extras
 *     (customer, lineItems, addresses) needed for single-FO use cases.
 *   - `fulfillmentGroup` (post-purchase only): FG-level state and a
 *     `fulfillmentOrders[]` array for multi-FO scenarios (split shipments).
 *
 * Per-FO fields (deliveryMethod, holds, per-FO lineItems) live under
 * `fulfillmentGroup.fulfillmentOrders[i]` because they can legitimately
 * differ across FOs.
 *
 * Authors don't normally see all of this — they declare a selector on their
 * code step and the framework prunes the universe to that subset before the
 * artifact runs.
 *
 * Availability across contexts: the same code step can be invoked both at
 * checkout-time (planning) and at post-purchase OMS engine time (execution).
 * Fields that don't exist yet at checkout (no order name, no FG, no product
 * lookups) are marked .optional() — the author must guard them.
 */

const addressSchema = z.object({
  country: z.string(),
  postalCode: z.string().nullable(),
  province: z.string().nullable(),
  city: z.string().nullable(),
  address1: z.string().nullable(),
  // True only after an address-verification step has run; checkout typically
  // hasn't done this yet.
  verified: z.boolean().optional(),
});

/**
 * Nine fixed slots for custom merchant fields. Mirrors the
 * `customString1..3`, `customNumber1..3`, `customDate1..3` shape on the
 * Shopify order subdoc. Used for two universe fields:
 *   - `order.customFields` (from customString1/customNumber1/customDate1)
 *   - `order.customAttributes` (from customAttributeString1/Number1/Date1)
 * Date slots are ISO strings so they cross the V8 isolate boundary cleanly.
 */
const customSlotsSchema = z.object({
  string1: z.string().nullable(),
  string2: z.string().nullable(),
  string3: z.string().nullable(),
  number1: z.number().nullable(),
  number2: z.number().nullable(),
  number3: z.number().nullable(),
  date1: z.string().nullable(),
  date2: z.string().nullable(),
  date3: z.string().nullable(),
});

const lineItemSchema = z.object({
  // Stable line-item id is assigned post-purchase; at checkout it's missing.
  id: z.string().optional(),
  sku: z.string().nullable(),
  quantity: z.number().int().nonnegative(),
  productId: z.string().nullable(),
  variantId: z.string().nullable(),
  // Product attributes require a server-side product lookup and aren't always
  // loaded at checkout — make them optional so callers must guard.
  productTitle: z.string().optional(),
  variantTitle: z.string().optional(),
  productType: z.string().optional(),
  vendor: z.string().optional(),
  productTags: z.array(z.string()).optional(),
  // Free-form key/value attributes attached to this line item at order
  // creation (e.g. gift wrap, custom engraving). Source: Shopify
  // line-item-level customAttributes array, projected as a record.
  customAttributes: z.record(z.string(), z.string()).optional(),
  priceCents: z.number().int(),
  // Current variant-level on-hand inventory for this line item's variant.
  // Source: Shopify variant.inventoryQuantity, snapshotted onto the FO line
  // item at order creation. Null when Shopify doesn't track inventory for
  // the variant; absent at checkout (no FO line item yet).
  variantInventoryQuantity: z.number().int().nullable().optional(),
});

/**
 * One entry in the team's parcel catalog. Outer dimensions are normalized to
 * inches and the empty-parcel weight to ounces. Carrier parcels don't expose
 * structured L/W/H, so their dimension/weight fields are null — guard them.
 */
const availableParcelSchema = z.object({
  id: z.string(),
  name: z.string(),
  // "custom" | "carrier".
  parcelType: z.string(),
  lengthInches: z.number().nullable(),
  widthInches: z.number().nullable(),
  heightInches: z.number().nullable(),
  weightOunces: z.number().nullable(),
});

const orderSchema = z.object({
  id: z.string(),
  // Assigned only after the order is placed; at checkout the order has no
  // name yet.
  name: z.string().optional(),
  totalCents: z.number().int(),
  // Taxes are computed at checkout finalization; may be 0 / absent earlier.
  totalTaxCents: z.number().int().optional(),
  currency: z.string(),
  // Order tags are applied post-purchase; at checkout the order has none yet.
  tags: z.array(z.string()).optional(),
  // Free-form order note. Optional — checkout-time orders haven't been
  // created in the source system yet.
  note: z.string().optional(),
  // Top-level platform channel ("online_store", "pos", etc.). Source:
  // FO.order.salesChannel.
  salesChannel: z.string().nullable().optional(),
  // Marketplace name where the order originated (e.g., "amazon", "etsy").
  // Source: FO.order.channelInformation.app.developerName.
  marketplace: z.string().nullable().optional(),
  // Aggregated max risk level across the order's risk assessments.
  // "low" | "medium" | "high" | null. Source: FO.order.risk.assessments[].
  fraudRiskLevel: z.string().nullable().optional(),
  // Custom field slots written via merchant "customMetadata" settings.
  // Source: FO.order.customString1/customNumber1/customDate1 etc.
  customFields: customSlotsSchema.optional(),
  // Custom attribute slots written via merchant "customAttributes" settings.
  // Source: FO.order.customAttributeString1/Number1/Date1 etc.
  customAttributes: customSlotsSchema.optional(),
  customer: z
    .object({
      id: z.string(),
      // Display name. Optional — checkout may have email only.
      name: z.string().nullable().optional(),
      email: z.string().nullable(),
      tags: z.array(z.string()),
      // Total lifetime orders by this customer. Optional — only populated
      // post-purchase.
      orderCount: z.number().int().nonnegative().optional(),
    })
    .nullable(),
  shippingAddress: addressSchema.nullable(),
  billingAddress: addressSchema.nullable(),
  // Aggregated view across all FOs for single-shipment cases. For per-FO
  // line items in split-shipment scenarios, read
  // fulfillmentGroup.fulfillmentOrders[].lineItems instead.
  lineItems: z.array(lineItemSchema),
});

const fulfillmentOrderSchema = z.object({
  // Same shape as order.lineItems but scoped to this specific FO. In
  // single-FO groups this matches the top-level array; in split shipments
  // it's the per-FO truth.
  lineItems: z.array(lineItemSchema),
  // Shopify delivery method classification for this FO. Source:
  // FO.deliveryMethod.kind.
  deliveryMethod: z.string().nullable(),
  // True when this FO has at least one active hold. Source:
  // FO.holds.length > 0.
  onHold: z.boolean(),
});

const fulfillmentGroupSchema = z.object({
  id: z.string(),
  weightGrams: z.number().nonnegative(),
  parcelId: z.string().nullable(),
  // Origin location id (matches the team's locations catalog server-side).
  // Source: FG.selectedLocationId.
  locationId: z.string().nullable(),
  // Per-store identifier in multi-store setups. Lives at the FG top level
  // in the source (not on the order subdoc).
  originStore: z.string().nullable(),
  // True when any FO in this group has at least one hold.
  onHold: z.boolean(),
  shippingService: z
    .object({ carrier: z.string(), service: z.string() })
    .nullable(),
  // Tag names currently applied to this FG. Source: FG.tags[].name.
  // Distinct from the team-level shipment-tag catalog (this is what's
  // actually on the FG, not what's available to apply).
  shipmentTags: z.array(z.string()),
  // One entry per FulfillmentOrder in the group. For unsplit shipments
  // there's a single entry whose lineItems mirror the top-level
  // order.lineItems.
  fulfillmentOrders: z.array(fulfillmentOrderSchema),
});

export const omsAutomationCodeStepInputSchema = z.object({
  order: orderSchema,
  // Fulfillment groups are created post-purchase; not available at checkout.
  fulfillmentGroup: fulfillmentGroupSchema.optional(),
  // USPS shipping zone between the FG's origin and destination addresses,
  // as a string (e.g. "1".."9", or "local"). Computed on-demand via the
  // team's EasyPost integration — selecting this field triggers an external
  // API call, so don't include it unless your code actually branches on it.
  // Null when the integration is missing or the lookup fails. Not available
  // at checkout (no FG yet).
  uspsZone: z.string().nullable().optional(),
  // The team's full parcel catalog (boxes/envelopes) with dimensions in
  // inches. A team-level lookup independent of the order, available in both
  // checkout and post-purchase. Loaded on demand only when the selector
  // references it. Pair with `productMetafields` to choose a parcel by
  // comparing product dimensions against parcel dimensions.
  availableParcels: z.array(availableParcelSchema).optional(),
  // Shopify product metafields for the products in this order, as a top-level
  // map keyed by product GID (matching lineItem.productId) →
  // { "namespace.key": value } (e.g. "global.LENGTH"). Look up directly via
  // productMetafields[lineItem.productId]. Modeled as a top-level map (rather
  // than a per-line-item field) so it's gated like any other top-level input —
  // the whole map is fetched once when the selector references
  // `productMetafields`, and the same product's metafields are shared across
  // line items. Absent unless selected; available in both checkout and
  // post-purchase.
  productMetafields: z
    .record(z.string(), z.record(z.string(), z.string()))
    .optional(),
  // Shopify variant metafields for the variants in this order, as a top-level
  // map keyed by variant GID (matching lineItem.variantId) →
  // { "namespace.key": value }. Same shape and loading as `productMetafields`
  // but scoped to the variant — look up via
  // variantMetafields[lineItem.variantId]. Use this when a product stores
  // attributes per variant (e.g. dimensions that differ by size); a typical
  // pattern is to read the variant value and fall back to the product value.
  // Absent unless the selector references `variantMetafields`; available in
  // both checkout and post-purchase.
  variantMetafields: z
    .record(z.string(), z.record(z.string(), z.string()))
    .optional(),
});

export const omsAutomationCodeStepTarget = {
  id: "oms-automation.code-step",
  displayName: "OMS automation code step",
  description:
    "A code-authored step in an OMS automation. Takes order and fulfillment context (narrowed by the step's selector) and returns a sequence of actions to enqueue.",
} as const satisfies Target;

/**
 * Starter source the merchant-app editor seeds the code panel with and the
 * MCP `codeStepContract` context handler hands to authoring agents. The
 * verify harness wires the contract's `Input` and `Output` types in as
 * ambient declarations, so no import is needed.
 */
export const omsAutomationCodeStepStarterCode = `// Edit this function. It receives the contract's Input and returns Output.
export default async (input: Input): Promise<Output> => {
  return { actions: [] };
};
`;

/**
 * Type declarations for the \`assignProductsToPackages\` helper, appended to the
 * contract's generated \`.d.ts\` at verify time so authoring agents and the
 * editor get full types and docs for the global.
 *
 * The return type is plain data (no action \`kind\`): the helper computes a
 * product-to-package plan, nothing more. Translating that plan into an OMS
 * action is the author's job until a dedicated \`assignProductsToPackages\`
 * action exists.
 */
const assignProductsToPackagesPreludeDts = `\
/** A linear measurement with its unit. The packer normalizes everything to inches. */
declare type Length = { unit: "in"; value: number } | { unit: "cm"; value: number };

/** A weight measurement with its unit. The packer normalizes everything to grams. */
declare type Weight =
  | { unit: "g"; value: number }
  | { unit: "kg"; value: number }
  | { unit: "oz"; value: number }
  | { unit: "lb"; value: number };

/** Outer dimensions of a product variant or a parcel. */
declare type Dims = { length: Length; width: Length; height: Length };

/** One line item (by its Input \`id\`) and how many units of it go in a package. */
declare type PackagedItem = { lineItemId: string; quantity: number };

/**
 * A single package (one physical parcel) and the items packed into it.
 * \`forced\` is true when an unpackable strategy produced this box by overriding
 * a constraint (oversized for the parcel, or over its weight cap) — i.e. it is
 * approximate, not a verified fit.
 */
declare type PackageAssignment = {
  parcelId: string;
  items: PackagedItem[];
  forced?: boolean;
};

/** Why a line item could not be packed. */
declare type UnpackableReason =
  | "missing_dimensions"
  | "no_parcel_fits"
  | "too_heavy"
  | "missing_weight";

/** A line left out of the packing, with the reason it could not be placed. */
declare type Unpackable = {
  lineItemId: string;
  variantId: string;
  quantity: number;
  reason: UnpackableReason;
};

/**
 * What to do with a line that hits a given unpackable reason.
 * - \`skip_and_report\`: exclude it, list it in \`unpackable\` (default).
 * - \`package_min_volume\` / \`package_max_volume\`: force each unit into its own
 *   smallest / largest parcel (the smallest/largest that fits dimensionally for
 *   \`too_heavy\`, else overall), flagged \`forced\`.
 * - \`fail\`: the whole packing fails (empty \`packages\`, \`failed: true\`).
 */
declare type UnpackableStrategy =
  | "skip_and_report"
  | "package_min_volume"
  | "package_max_volume"
  | "fail";

/** \`too_heavy\` also supports packing the item with its parcel's weight cap softened. */
declare type TooHeavyStrategy = UnpackableStrategy | "combine_over_weight";

/** Optional packing controls. */
declare type AssignProductsToPackagesOptions = {
  /**
   * Wall clearance reserved on every side of every parcel for packing material
   * (dunnage). Shrinks each parcel's usable interior by twice this on each axis.
   */
  clearance?: Length;
  /**
   * Per-error-type handling for lines that can't be cleanly packed. Each
   * reason defaults to \`"skip_and_report"\`.
   */
  unpackablePolicy?: {
    missingDimensions?: UnpackableStrategy;
    noParcelFits?: UnpackableStrategy;
    tooHeavy?: TooHeavyStrategy;
    missingWeight?: UnpackableStrategy;
  };
};

declare type AssignProductsToPackagesInput = {
  /**
   * Lines to pack. \`variantId\` keys into \`variantDimensions\` / \`variantWeights\`.
   * \`ownPackage\` isolates a line from other products: \`"per_unit"\` puts each
   * unit alone in its own parcel (fragile / hazmat), \`"per_line"\` packs the
   * line's units together but never mixed with other products. Omit to pack
   * the line normally with everything else.
   */
  lineItems: {
    lineItemId: string;
    variantId: string;
    quantity: number;
    ownPackage?: "per_unit" | "per_line";
  }[];
  /** Per-variant outer dimensions, keyed by variant id. */
  variantDimensions: Record<string, Dims>;
  /**
   * Per-variant weight, keyed by variant id. Optional; a variant with no entry
   * is treated as weightless. Used with \`parcelMaxWeights\`.
   */
  variantWeights?: Record<string, Weight>;
  /** Candidate parcels, keyed by parcel id (e.g. from \`availableParcels[].id\`). */
  parcels: Record<string, Dims>;
  /**
   * Per-parcel maximum content weight, keyed by parcel id. Optional; a parcel
   * with no entry has no weight limit. A box is never filled past this.
   */
  parcelMaxWeights?: Record<string, Weight>;
  /** Optional packing controls (clearance, ...). */
  options?: AssignProductsToPackagesOptions;
};

declare type AssignProductsToPackagesResult = {
  /**
   * One entry per physical box. Two entries may share a \`parcelId\` (the plan
   * uses two boxes of the same parcel), and a single line item may be split
   * across entries when its units don't all fit one box.
   */
  packages: PackageAssignment[];
  /** Lines not packed (per \`skip_and_report\`): unknown dims, no fit, too heavy. */
  unpackable: Unpackable[];
  /**
   * True when a \`"fail"\` strategy fired: \`packages\` is empty and every error
   * line is listed in \`unpackable\`. Absent otherwise.
   */
  failed?: boolean;
};

/**
 * 3D bin-packs line items into the supplied parcels (empty-maximal-spaces
 * heuristic) and returns the package assignment plus any lines it could not
 * pack. Never throws on data.
 *
 * - Dimensions carry their unit (\`"in"\` | \`"cm"\`) and are normalized to inches.
 * - A line whose variant is absent from \`variantDimensions\` (or has
 *   non-positive dims) is returned in \`unpackable\` as \`"missing_dimensions"\`.
 * - A line whose item fits no parcel in any rotation is returned in
 *   \`unpackable\` as \`"no_parcel_fits"\`; one that fits dimensionally but
 *   exceeds the weight cap of every parcel that fits it is \`"too_heavy"\`.
 * - A line that fits dimensionally but has no configured weight while a finite
 *   weight cap applies to a parcel it fits is \`"missing_weight"\`.
 * - \`parcelMaxWeights\` caps content weight per box; \`options.clearance\`
 *   reserves wall space; \`ownPackage\` isolates a line (\`"per_unit"\` /
 *   \`"per_line"\`) from other products.
 * - \`options.unpackablePolicy\` chooses what to do per error type (skip and
 *   report, force into a min/max-volume parcel, soften the weight cap, or fail).
 * - Empty \`lineItems\` yields empty \`packages\` and \`unpackable\`.
 *
 * No dedicated OMS action exists yet, so translate the result into a
 * \`setParcel\` action yourself:
 *
 *   const { packages } = assignProductsToPackages({ lineItems, variantDimensions, parcels });
 *   return { actions: [{ kind: "setParcel", parcelIds: packages.map((p) => p.parcelId) }] };
 */
declare function assignProductsToPackages(
  input: AssignProductsToPackagesInput,
): AssignProductsToPackagesResult;
`;

/**
 * JavaScript prepended to the transpiled artifact at invoke time. Defines the
 * \`assignProductsToPackages\` global the \`.d.ts\` declares — an empty-maximal-
 * spaces 3D bin packer. Pure: it reads no host state and is deterministic; it
 * never throws on data, returning un-placeable lines in \`unpackable\` instead.
 *
 * Ported from the merchant-authored EMS packer, adapted to (1) take normalized,
 * unit-tagged inputs instead of reading the contract Input, (2) carry
 * \`lineItemId\` identity through packing so it can emit per-box allocations, and
 * (3) report un-placeable lines rather than silently dropping them.
 */
const assignProductsToPackagesPreludeJs = `\
const assignProductsToPackages = (input) => {
  const lineItems = (input && input.lineItems) || [];
  const variantDimensions = (input && input.variantDimensions) || {};
  const variantWeights = (input && input.variantWeights) || {};
  const parcelDimensions = (input && input.parcels) || {};
  const parcelMaxWeights = (input && input.parcelMaxWeights) || {};
  const options = (input && input.options) || {};

  // Exact EMS packing is dense but ~O(N^2.3) on adversarial (non-tiling)
  // inputs (measured: ~34ms at N=100, ~90ms at N=200, ~270ms at N=300). This
  // step can run on the checkout path, where Shopify times the request out, so
  // the cap is kept low to bound worst-case latency; above it the (linear but
  // lower-density) fast path takes over. Real few-SKU orders tile ~100x faster.
  const MAX_ITEMS_FOR_EXACT_PACKING = 100;
  // Volume target for the fast path's initial grouping only. EMS placement
  // (with spill) is what guarantees fit, so this is NOT a safety margin —
  // 1.0 lets EMS arbitrate density rather than pre-shrinking the estimate;
  // anything lower just creates more, emptier boxes for no correctness gain.
  const PACKING_FILL_FACTOR = 1.0;
  // Absorbs unit-conversion float error (e.g. 5.08cm -> 2in) so a dimension
  // equal to a box's is not rejected by a sub-micron rounding artifact.
  const FIT_EPSILON = 1e-6;
  // Caps EMS work per box on the fast path so its cost stays ~linear in N even
  // when a large parcel could hold hundreds of small items by volume (EMS on
  // one huge box is the O(S^2) blowup). Trades packing density for bounded
  // latency on very large carts.
  const MAX_ITEMS_PER_FAST_BOX = 64;

  const toInches = (length) => {
    if (!length || typeof length.value !== "number" || !isFinite(length.value)) {
      return NaN;
    }
    if (length.unit === "cm") return length.value / 2.54;
    if (length.unit === "in") return length.value;
    return NaN;
  };

  const toGrams = (weight) => {
    if (!weight || typeof weight.value !== "number" || !isFinite(weight.value)) {
      return NaN;
    }
    if (weight.unit === "g") return weight.value;
    if (weight.unit === "kg") return weight.value * 1000;
    if (weight.unit === "oz") return weight.value * 28.349523125;
    if (weight.unit === "lb") return weight.value * 453.59237;
    return NaN;
  };

  // Wall clearance (per side) reserved for packing material, subtracted from
  // every parcel's interior on each axis. Applies to both paths via boxTypes.
  const clearanceInches = (() => {
    const c = options.clearance ? toInches(options.clearance) : 0;
    return isFinite(c) && c > 0 ? c : 0;
  })();

  const parcelWeightGrams = (parcelId) => {
    const w = parcelMaxWeights[parcelId] ? toGrams(parcelMaxWeights[parcelId]) : NaN;
    return isFinite(w) && w > 0 ? w : Infinity;
  };

  // Variant weight in grams, or null when none is configured (or it's invalid).
  // Null is treated as weightless unless a finite parcel cap applies to the
  // item, in which case it surfaces as "missing_weight".
  const rawVariantWeight = (variantId) => {
    const w = variantWeights[variantId] ? toGrams(variantWeights[variantId]) : NaN;
    return isFinite(w) && w > 0 ? w : null;
  };

  const normalizeDims = (dims) => {
    if (!dims) return null;
    const l = toInches(dims.length);
    const w = toInches(dims.width);
    const h = toInches(dims.height);
    if (!(l > 0 && w > 0 && h > 0)) return null;
    return [l, w, h];
  };

  const volume = (d) => d[0] * d[1] * d[2];
  const descTriple = (a, b, c) => [a, b, c].sort((x, y) => y - x);

  const rotations = (d) => {
    const perms = [
      [d[0], d[1], d[2]],
      [d[0], d[2], d[1]],
      [d[1], d[0], d[2]],
      [d[1], d[2], d[0]],
      [d[2], d[0], d[1]],
      [d[2], d[1], d[0]],
    ];
    const seen = new Set();
    const out = [];
    for (const p of perms) {
      const key = p.join(",");
      if (!seen.has(key)) {
        seen.add(key);
        out.push(p);
      }
    }
    return out;
  };

  const fitsSorted = (item, box) => {
    const a = descTriple(item[0], item[1], item[2]);
    const b = descTriple(box[0], box[1], box[2]);
    return (
      a[0] <= b[0] + FIT_EPSILON &&
      a[1] <= b[1] + FIT_EPSILON &&
      a[2] <= b[2] + FIT_EPSILON
    );
  };

  const contains = (a, b) =>
    a.x <= b.x &&
    a.y <= b.y &&
    a.z <= b.z &&
    a.x + a.w >= b.x + b.w &&
    a.y + a.h >= b.y + b.h &&
    a.z + a.d >= b.z + b.d;

  const prune = (spaces) =>
    spaces.filter(
      (b, i) =>
        !spaces.some(
          (a, j) => j !== i && contains(a, b) && (!contains(b, a) || j < i),
        ),
    );

  const newBox = (type) => ({
    typeId: type.id,
    maxWeight: type.maxWeight,
    weightUsed: 0,
    spaces: [
      { x: 0, y: 0, z: 0, w: type.dims[0], h: type.dims[1], d: type.dims[2] },
    ],
    items: [],
  });

  const canTakeWeight = (box, item) =>
    box.weightUsed + item.weight <= box.maxWeight + FIT_EPSILON;

  const bestPlacement = (box, base) => {
    const itemVol = volume(base);
    let best = null;
    for (let si = 0; si < box.spaces.length; si++) {
      const s = box.spaces[si];
      const spaceVol = s.w * s.h * s.d;
      for (const rot of rotations(base)) {
        if (
          rot[0] <= s.w + FIT_EPSILON &&
          rot[1] <= s.h + FIT_EPSILON &&
          rot[2] <= s.d + FIT_EPSILON
        ) {
          const waste = spaceVol - itemVol;
          if (best === null || waste < best.waste) {
            best = { spaceIndex: si, rot: rot, waste: waste };
          }
        }
      }
    }
    return best;
  };

  const place = (box, item, spaceIndex, rot) => {
    const s = box.spaces[spaceIndex];
    const ix = s.x;
    const iy = s.y;
    const iz = s.z;
    const iX = s.x + rot[0];
    const iY = s.y + rot[1];
    const iZ = s.z + rot[2];
    const next = [];
    for (const sp of box.spaces) {
      const spX = sp.x + sp.w;
      const spY = sp.y + sp.h;
      const spZ = sp.z + sp.d;
      const intersects =
        sp.x < iX &&
        spX > ix &&
        sp.y < iY &&
        spY > iy &&
        sp.z < iZ &&
        spZ > iz;
      if (!intersects) {
        next.push(sp);
        continue;
      }
      if (ix > sp.x) next.push({ x: sp.x, y: sp.y, z: sp.z, w: ix - sp.x, h: sp.h, d: sp.d });
      if (spX > iX) next.push({ x: iX, y: sp.y, z: sp.z, w: spX - iX, h: sp.h, d: sp.d });
      if (iy > sp.y) next.push({ x: sp.x, y: sp.y, z: sp.z, w: sp.w, h: iy - sp.y, d: sp.d });
      if (spY > iY) next.push({ x: sp.x, y: iY, z: sp.z, w: sp.w, h: spY - iY, d: sp.d });
      if (iz > sp.z) next.push({ x: sp.x, y: sp.y, z: sp.z, w: sp.w, h: sp.h, d: iz - sp.z });
      if (spZ > iZ) next.push({ x: sp.x, y: sp.y, z: iZ, w: sp.w, h: sp.h, d: spZ - iZ });
    }
    box.spaces = prune(next);
    box.items.push(item);
    box.weightUsed += item.weight;
  };

  const tryPlace = (box, item) => {
    if (!canTakeWeight(box, item)) return false;
    const p = bestPlacement(box, item.dims);
    if (!p) return false;
    place(box, item, p.spaceIndex, p.rot);
    return true;
  };

  const sortedTypesByVolume = (types) =>
    types.slice().sort((a, b) => volume(a.dims) - volume(b.dims));

  const smallestTypeFor = (itemList, types) => {
    for (const type of sortedTypesByVolume(types)) {
      const box = newBox(type);
      if (itemList.every((it) => tryPlace(box, it))) return type.id;
    }
    return null;
  };

  const packIntoType = (items, type) => {
    const boxes = [];
    for (const item of items) {
      // A fresh box of this type must hold the item by both dimension and weight,
      // else no copy of this type can pack the whole set.
      if (
        !fitsSorted(item.dims, type.dims) ||
        item.weight > type.maxWeight + FIT_EPSILON
      ) {
        return null;
      }
      let target = null;
      let bestWaste = Infinity;
      for (let bi = 0; bi < boxes.length; bi++) {
        if (!canTakeWeight(boxes[bi], item)) continue;
        const p = bestPlacement(boxes[bi], item.dims);
        if (p && p.waste < bestWaste) {
          bestWaste = p.waste;
          target = { boxIndex: bi, spaceIndex: p.spaceIndex, rot: p.rot };
        }
      }
      if (target) {
        place(boxes[target.boxIndex], item, target.spaceIndex, target.rot);
      } else {
        const box = newBox(type);
        tryPlace(box, item);
        boxes.push(box);
      }
    }
    return boxes;
  };

  // Exact path: pack into copies of each viable single box type, keep the plan
  // with the fewest boxes, then downsize each box to the smallest type that
  // still holds its contents.
  const packExact = (items, types) => {
    let chosen = null;
    for (const type of sortedTypesByVolume(types)) {
      const boxes = packIntoType(items, type);
      if (!boxes) continue;
      if (chosen === null || boxes.length < chosen.length) chosen = boxes;
    }
    if (!chosen) return null;
    return chosen.map((box) => ({
      typeId: smallestTypeFor(box.items, types) || box.typeId,
      items: box.items,
    }));
  };

  // Fast path for large orders. A volume-based first-fit-decreasing grouping
  // bounds the number of items per box (keeping each EMS pass cheap), then a
  // real EMS placement of each group makes the emitted allocations
  // geometrically valid — items the volume estimate over-committed spill into
  // the next round. Each group's first item always places (it fits the type),
  // so the queue strictly shrinks and this terminates.
  const fastPackIntoType = (items, type) => {
    for (const item of items) {
      if (
        !fitsSorted(item.dims, type.dims) ||
        item.weight > type.maxWeight + FIT_EPSILON
      ) {
        return null;
      }
    }
    const capacity = volume(type.dims) * PACKING_FILL_FACTOR;
    const boxes = [];
    let queue = items;
    while (queue.length > 0) {
      const groups = [];
      for (const item of queue) {
        const vol = volume(item.dims);
        let placed = false;
        for (const g of groups) {
          if (g.items.length < MAX_ITEMS_PER_FAST_BOX && g.remaining >= vol) {
            g.remaining -= vol;
            g.items.push(item);
            placed = true;
            break;
          }
        }
        if (!placed) groups.push({ remaining: capacity - vol, items: [item] });
      }
      const overflow = [];
      for (const g of groups) {
        const box = newBox(type);
        for (const item of g.items) {
          if (!tryPlace(box, item)) overflow.push(item);
        }
        boxes.push({ typeId: type.id, items: box.items });
      }
      queue = overflow;
    }
    return boxes;
  };

  const packFast = (items, types) => {
    let best = null;
    for (const type of types) {
      const boxes = fastPackIntoType(items, type);
      if (!boxes) continue;
      const vol = volume(type.dims);
      if (
        best === null ||
        boxes.length < best.boxes.length ||
        (boxes.length === best.boxes.length && vol < best.vol)
      ) {
        best = { boxes: boxes, vol: vol };
      }
    }
    return best ? best.boxes : null;
  };

  // Parcels with full, valid dimensions become box types. Wall clearance
  // shrinks the usable interior on each axis; a parcel whose interior collapses
  // to <= 0 is dropped. maxWeight defaults to Infinity when none is given. The
  // output still references the real parcel id, not the shrunk geometry.
  const boxTypes = [];
  for (const parcelId of Object.keys(parcelDimensions)) {
    const dims = normalizeDims(parcelDimensions[parcelId]);
    if (!dims) continue;
    const inner = [
      dims[0] - 2 * clearanceInches,
      dims[1] - 2 * clearanceInches,
      dims[2] - 2 * clearanceInches,
    ];
    if (!(inner[0] > 0 && inner[1] > 0 && inner[2] > 0)) continue;
    boxTypes.push({
      id: parcelId,
      dims: inner,
      maxWeight: parcelWeightGrams(parcelId),
    });
  }

  // Per-error-type strategy for lines that can't be cleanly packed. Each
  // defaults to "skip_and_report" (exclude + list in \`unpackable\`).
  const policy = options.unpackablePolicy || {};
  const strategyFor = (reason) => {
    if (reason === "missing_dimensions") {
      return policy.missingDimensions || "skip_and_report";
    }
    if (reason === "no_parcel_fits") return policy.noParcelFits || "skip_and_report";
    if (reason === "missing_weight") return policy.missingWeight || "skip_and_report";
    return policy.tooHeavy || "skip_and_report"; // too_heavy
  };
  const pickParcel = (candidates, strategy) => {
    if (candidates.length === 0) return null;
    const sorted = sortedTypesByVolume(candidates);
    return strategy === "package_max_volume" ? sorted[sorted.length - 1] : sorted[0];
  };

  // Expand each packable line into one unit per quantity (tagged with its
  // lineItemId and weight); apply the configured strategy to lines we can't
  // measure, can't fit, or that are too heavy. ownPackage lines are routed to
  // their own boxes.
  const perUnitUnits = []; // ownPackage "per_unit" -> one box per unit
  const perLineGroups = []; // ownPackage "per_line" -> one packing per line
  const sharedUnits = [];
  const skipReport = []; // skip_and_report -> output \`unpackable\`
  const allErrors = []; // every error line, surfaced when a "fail" strategy fires
  const forcedBoxes = [];
  let failed = false;
  const routeUnits = (li, unit) => {
    if (li.ownPackage === "per_unit") {
      for (let i = 0; i < li.quantity; i++) perUnitUnits.push(unit);
    } else if (li.ownPackage === "per_line") {
      const group = [];
      for (let i = 0; i < li.quantity; i++) group.push(unit);
      perLineGroups.push(group);
    } else {
      for (let i = 0; i < li.quantity; i++) sharedUnits.push(unit);
    }
  };
  for (const li of lineItems) {
    const dims = normalizeDims(variantDimensions[li.variantId]);
    const dimFits = dims ? boxTypes.filter((t) => fitsSorted(dims, t.dims)) : [];
    let reason = null;
    let weight = 0;
    if (!dims) {
      reason = "missing_dimensions";
    } else if (dimFits.length === 0) {
      reason = "no_parcel_fits";
    } else {
      const w = rawVariantWeight(li.variantId);
      if (w === null) {
        // Unknown weight only matters if a finite cap applies to a parcel this
        // item fits; otherwise it packs weightless.
        if (dimFits.some((t) => isFinite(t.maxWeight))) reason = "missing_weight";
      } else {
        weight = w;
        if (!dimFits.some((t) => weight <= t.maxWeight + FIT_EPSILON)) {
          reason = "too_heavy";
        }
      }
    }

    if (!reason) {
      routeUnits(li, { lineItemId: li.lineItemId, dims: dims, weight: weight });
      continue;
    }

    const errorEntry = {
      lineItemId: li.lineItemId,
      variantId: li.variantId,
      quantity: li.quantity,
      reason: reason,
    };
    allErrors.push(errorEntry);
    const strategy = strategyFor(reason);

    if (strategy === "fail") {
      failed = true;
    } else if (strategy === "combine_over_weight" && reason === "too_heavy") {
      // Pack normally but ignore this item's weight against the cap (soft cap);
      // any box it lands in is flagged forced.
      routeUnits(li, {
        lineItemId: li.lineItemId,
        dims: dims,
        weight: 0,
        soft: true,
      });
    } else if (
      strategy === "package_min_volume" ||
      strategy === "package_max_volume"
    ) {
      // too_heavy / missing_weight fit some parcel dimensionally (weight gate
      // overridden); otherwise no parcel fits, so force into one anyway.
      const candidates =
        reason === "too_heavy" || reason === "missing_weight" ? dimFits : boxTypes;
      const parcel = pickParcel(candidates, strategy);
      if (!parcel) {
        skipReport.push(errorEntry);
      } else {
        for (let i = 0; i < li.quantity; i++) {
          forcedBoxes.push({
            typeId: parcel.id,
            items: [{ lineItemId: li.lineItemId }],
            forced: true,
          });
        }
      }
    } else {
      skipReport.push(errorEntry); // skip_and_report (default / fallback)
    }
  }

  if (failed) {
    return { packages: [], unpackable: allErrors, failed: true };
  }

  // Pack a set of units into boxes: exact/fast by size, with a one-box-per-unit
  // fallback when no single parcel type holds them all (non-nested catalog).
  const packUnits = (units) => {
    if (units.length === 0) return [];
    const sorted = units.slice().sort((a, b) => volume(b.dims) - volume(a.dims));
    let boxes =
      sorted.length > MAX_ITEMS_FOR_EXACT_PACKING
        ? packFast(sorted, boxTypes)
        : packExact(sorted, boxTypes);
    if (!boxes) {
      boxes = sorted.map((unit) => ({
        typeId: smallestTypeFor([unit], boxTypes),
        items: [unit],
      }));
    }
    return boxes;
  };

  // "per_unit": each unit alone in the smallest parcel that holds it.
  const perUnitBoxes = perUnitUnits.map((unit) => ({
    typeId: smallestTypeFor([unit], boxTypes),
    items: [unit],
  }));
  // "per_line": each line packed on its own, never mixed with other products.
  const perLineBoxes = [];
  for (const group of perLineGroups) {
    for (const box of packUnits(group)) perLineBoxes.push(box);
  }
  // Everything else shares.
  const sharedBoxes = packUnits(sharedUnits);

  const boxes = perUnitBoxes
    .concat(perLineBoxes)
    .concat(sharedBoxes)
    .concat(forcedBoxes);

  const packages = boxes.map((box) => {
    const byLineItem = new Map();
    for (const it of box.items) {
      byLineItem.set(it.lineItemId, (byLineItem.get(it.lineItemId) || 0) + 1);
    }
    const items = [];
    byLineItem.forEach((quantity, lineItemId) => {
      items.push({ lineItemId: lineItemId, quantity: quantity });
    });
    const pkg = { parcelId: box.typeId, items: items };
    if (box.forced || box.items.some((it) => it.soft)) pkg.forced = true;
    return pkg;
  });

  return { packages: packages, unpackable: skipReport };
};
`;

const emptyCustomSlots = {
  string1: null,
  string2: null,
  string3: null,
  number1: null,
  number2: null,
  number3: null,
  date1: null,
  date2: null,
  date3: null,
};

const sampleLineItem = {
  id: "li_1",
  sku: "SKU-001",
  quantity: 2,
  productId: "prod_1",
  variantId: "var_1",
  productTitle: "Widget",
  variantTitle: "Large",
  productType: "Accessories",
  vendor: "Acme",
  productTags: ["fragile"],
  customAttributes: { engraving: "Happy Birthday" },
  priceCents: 4999,
  variantInventoryQuantity: 42,
};

export const omsAutomationCodeStepContractV1: Contract = {
  id: "oms-automation.code-step@2026-05.1",
  targetId: omsAutomationCodeStepTarget.id,
  inputSchema: omsAutomationCodeStepInputSchema,
  outputSchema: omsAutomationCodeStepOutputSchema,
  tools: [],
  runtimePreludeJs: assignProductsToPackagesPreludeJs,
  runtimePreludeDts: assignProductsToPackagesPreludeDts,
  sampleInput: {
    order: {
      id: "order_123",
      name: "#1001",
      totalCents: 9999,
      totalTaxCents: 800,
      currency: "USD",
      tags: ["vip"],
      note: "Please leave at front door",
      salesChannel: "online_store",
      marketplace: null,
      fraudRiskLevel: "low",
      customFields: emptyCustomSlots,
      customAttributes: emptyCustomSlots,
      customer: {
        id: "cust_456",
        name: "Jane Doe",
        email: "jane@example.com",
        tags: ["repeat-buyer"],
        orderCount: 5,
      },
      shippingAddress: {
        country: "US",
        postalCode: "94110",
        province: "CA",
        city: "San Francisco",
        address1: "123 Main St",
        verified: true,
      },
      billingAddress: {
        country: "US",
        postalCode: "94110",
        province: "CA",
        city: "San Francisco",
        address1: "123 Main St",
      },
      lineItems: [sampleLineItem],
    },
    fulfillmentGroup: {
      id: "fg_1",
      weightGrams: 500,
      parcelId: "parcel_1",
      locationId: "loc_1",
      originStore: "main",
      onHold: false,
      shippingService: { carrier: "ups", service: "ground" },
      shipmentTags: ["priority"],
      fulfillmentOrders: [
        {
          lineItems: [sampleLineItem],
          deliveryMethod: "shipping",
          onHold: false,
        },
      ],
    },
    uspsZone: "4",
    availableParcels: [
      {
        id: "parcel_1",
        name: "Small Box",
        parcelType: "custom",
        lengthInches: 6,
        widthInches: 4,
        heightInches: 2,
        weightOunces: 1.5,
      },
    ],
    // Keyed by product GID, matching lineItem.productId.
    productMetafields: {
      prod_1: {
        "global.LENGTH": "4",
        "global.WIDTH": "22",
        "global.HEIGHT": "3.5",
      },
    },
    // Keyed by variant GID, matching lineItem.variantId.
    variantMetafields: { var_1: { "global.LENGTH": "5" } },
  } satisfies z.infer<typeof omsAutomationCodeStepInputSchema>,
};
